// ============================================================================
// GESTIMMONY — Alertes intelligentes calendaires (bailleur)
// ============================================================================
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle, Calendar, Clock, TrendingUp, FileWarning, Receipt,
  Home, ArrowRight, CheckCircle2, Bell,
} from "lucide-react";

const TYPE_ICONS = {
  irl_revision: TrendingUp,
  lease_end: Calendar,
  unpaid: Receipt,
  dpe_expiring: FileWarning,
};

const SEV_STYLES = {
  critical: {
    dot: "bg-rose-500",
    badge: "bg-rose-100 text-rose-800 border-rose-200",
    ring: "ring-rose-200",
    icon: "text-rose-600 bg-rose-50",
    label: "Urgent",
  },
  warning: {
    dot: "bg-amber-500",
    badge: "bg-amber-100 text-amber-800 border-amber-200",
    ring: "ring-amber-200",
    icon: "text-amber-600 bg-amber-50",
    label: "À prévoir",
  },
  info: {
    dot: "bg-sky-500",
    badge: "bg-sky-100 text-sky-800 border-sky-200",
    ring: "ring-sky-200",
    icon: "text-sky-600 bg-sky-50",
    label: "Info",
  },
};

const TYPE_LABEL = {
  irl_revision: "Révision IRL",
  lease_end: "Échéance bail",
  unpaid: "Impayé",
  dpe_expiring: "DPE",
};

export default function AlertsPage() {
  const [data, setData] = useState({ alerts: [], count: 0, critical: 0, warning: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/alerts")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = data.alerts.filter(a =>
    filter === "all" ? true :
    filter === "critical" ? a.severity === "critical" :
    a.type === filter
  );

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Alertes intelligentes</h1>
        <p className="text-sm text-slate-500 mt-1">
          Gestimmony surveille pour vous les échéances importantes : révisions IRL, fin de baux, impayés, DPE…
        </p>
      </div>

      {/* Résumé */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard
          testid="alert-summary-critical"
          value={data.critical}
          label="Urgentes"
          icon={AlertTriangle}
          color="rose"
          active={filter === "critical"}
          onClick={() => setFilter("critical")}
        />
        <SummaryCard
          testid="alert-summary-total"
          value={data.count}
          label="Toutes"
          icon={Bell}
          color="teal"
          active={filter === "all"}
          onClick={() => setFilter("all")}
        />
        <SummaryCard
          testid="alert-summary-irl"
          value={data.alerts.filter(a => a.type === "irl_revision").length}
          label="Révisions IRL"
          icon={TrendingUp}
          color="emerald"
          active={filter === "irl_revision"}
          onClick={() => setFilter("irl_revision")}
        />
        <SummaryCard
          testid="alert-summary-end"
          value={data.alerts.filter(a => a.type === "lease_end").length}
          label="Échéances baux"
          icon={Calendar}
          color="amber"
          active={filter === "lease_end"}
          onClick={() => setFilter("lease_end")}
        />
      </div>

      {/* Liste */}
      <Card className="p-6" data-testid="alerts-list-card">
        {loading ? (
          <div className="text-center py-10 text-slate-400">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12" data-testid="alerts-empty">
            <div className="h-14 w-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-3">
              <CheckCircle2 className="h-7 w-7 text-emerald-600" />
            </div>
            <div className="font-heading text-lg font-semibold text-slate-900">
              Tout est à jour !
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Aucune alerte à traiter pour l'instant. Nous vous préviendrons dès qu'une échéance approche.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {filtered.map((a) => (
              <AlertRow key={a.id} alert={a} onCta={() => navigate(a.cta?.route || "#")} />
            ))}
          </ul>
        )}
      </Card>

      {/* Explication */}
      <Card className="p-5 bg-gradient-to-br from-teal-50/40 to-emerald-50/40 border-teal-100">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center shrink-0">
            <Bell className="h-5 w-5" />
          </div>
          <div className="text-sm text-slate-700">
            <div className="font-heading font-semibold text-slate-900 mb-1">
              Comment fonctionnent les alertes ?
            </div>
            <p className="text-slate-600">
              Chaque connexion, Gestimmony recalcule les échéances à partir de vos baux, quittances et biens.
              <br />Aucune configuration n'est nécessaire : dès qu'un événement approche, il apparaît ici.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ------- Composants ------- //
function SummaryCard({ testid, value, label, icon: Icon, color, active, onClick }) {
  const colors = {
    rose: "text-rose-600 bg-rose-50 border-rose-100",
    teal: "text-teal-600 bg-teal-50 border-teal-100",
    emerald: "text-emerald-600 bg-emerald-50 border-emerald-100",
    amber: "text-amber-600 bg-amber-50 border-amber-100",
  }[color];
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left p-4 rounded-xl border transition-all ${
        active ? "bg-white ring-2 ring-teal-400 shadow-md" : "bg-white/60 hover:bg-white hover:shadow-sm"
      } border-stone-200`}
    >
      <div className={`h-9 w-9 rounded-lg ${colors} flex items-center justify-center mb-2 border`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="font-heading text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </button>
  );
}

function AlertRow({ alert, onCta }) {
  const style = SEV_STYLES[alert.severity] || SEV_STYLES.info;
  const Icon = TYPE_ICONS[alert.type] || AlertTriangle;
  return (
    <li className="py-4 flex items-start gap-4" data-testid={`alert-item-${alert.id}`}>
      <div className={`h-10 w-10 rounded-lg ${style.icon} flex items-center justify-center shrink-0 border ${style.ring}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-heading font-semibold text-slate-900">{alert.title}</span>
          <Badge variant="outline" className={`${style.badge} text-[10px] px-2 py-0`}>{style.label}</Badge>
          <Badge variant="outline" className="text-[10px] px-2 py-0 bg-slate-50 text-slate-600 border-slate-200">
            {TYPE_LABEL[alert.type] || alert.type}
          </Badge>
        </div>
        <p className="text-sm text-slate-600 mt-1">{alert.message}</p>
        {alert.context && (
          <p className="text-xs text-slate-400 mt-0.5">{alert.context}</p>
        )}
      </div>
      {alert.cta && (
        <Button
          size="sm"
          onClick={onCta}
          data-testid={`alert-cta-${alert.id}`}
          className="shrink-0 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
        >
          {alert.cta.label} <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      )}
    </li>
  );
}

// Petit composant réutilisable pour Dashboard
export function AlertsWidget() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/alerts").then(({ data }) => setData(data)); }, []);

  if (!data || data.count === 0) return null;

  const top3 = data.alerts.slice(0, 3);
  return (
    <Card className="p-5 border-l-4 border-l-amber-500 bg-gradient-to-r from-amber-50/60 to-transparent" data-testid="alerts-widget">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
            <Bell className="h-5 w-5" />
          </div>
          <div>
            <div className="font-heading font-bold text-slate-900">
              {data.critical > 0 && <span className="text-rose-600">{data.critical} urgente{data.critical > 1 ? "s" : ""} · </span>}
              {data.count} alerte{data.count > 1 ? "s" : ""}
            </div>
            <div className="text-xs text-slate-500">
              Gestimmony vous signale ce qu'il faut traiter cette semaine
            </div>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          data-testid="alerts-widget-see-all"
          onClick={() => navigate("/app/alertes")}
        >
          Tout voir <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
      <ul className="space-y-2">
        {top3.map((a) => {
          const style = SEV_STYLES[a.severity] || SEV_STYLES.info;
          const Icon = TYPE_ICONS[a.type] || AlertTriangle;
          return (
            <li key={a.id} className="flex items-center gap-3 text-sm bg-white/60 rounded-lg px-3 py-2 border border-stone-100">
              <div className={`h-1.5 w-1.5 rounded-full ${style.dot} shrink-0`} />
              <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <span className="font-medium text-slate-800 truncate flex-1">{a.title}</span>
              <span className="text-xs text-slate-500 truncate hidden sm:inline">{a.message}</span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
